
import React, { useState, useEffect } from 'react';
import { dbService } from '../services/dbService.ts';
import { UserRole } from '../types.ts';

const ProfileView = ({ user }) => {
  const isPro = user.role === UserRole.PROFESSIONAL;
  const initial = isPro ? dbService.getProfessionalProfile(user.id) : dbService.getStudentProfile(user.id);
  const [formData, setFormData] = useState<any>(initial || {
    targetStudentGPA: 'any',
    targetStudentIndustry: 'any',
    targetStudentDegree: 'any'
  });
  const [isSaving, setIsSaving] = useState(false);
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    // Sync initial state with current DOM
    setIsDark(document.documentElement.classList.contains('dark'));
  }, []);

  const toggleTheme = () => {
    if (document.documentElement.classList.contains('dark')) {
      document.documentElement.classList.remove('dark');
      localStorage.theme = 'light';
      setIsDark(false);
    } else {
      document.documentElement.classList.add('dark');
      localStorage.theme = 'dark';
      setIsDark(true);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    await new Promise(r => setTimeout(r, 1000));
    const finalData = { ...formData, updatedAt: Date.now() };
    
    // Helper for array fields
    const splitArray = (val: string | string[]) => 
      typeof val === 'string' ? val.split(',').map(s => s.trim()).filter(Boolean) : val;

    if (isPro) {
      if (typeof finalData.workHistory === 'string') {
        finalData.workHistory = finalData.workHistory.split(';').map(item => {
          const parts = item.split(',').map(s => s.trim());
          return {
            role: parts[0] || 'Past Role',
            company: parts[1] || 'Past Company',
            location: parts[2] || '',
            duration: parts[3] || ''
          };
        }).filter(h => h.company);
      }
      finalData.topics = splitArray(finalData.topics);
      finalData.hobbies = splitArray(finalData.hobbies);
      dbService.saveProfessionalProfile(finalData);
    } else {
      finalData.interests = splitArray(finalData.interests);
      finalData.mentorshipTopics = splitArray(finalData.mentorshipTopics);
      finalData.hobbies = splitArray(finalData.hobbies);
      dbService.saveStudentProfile(finalData);
    }
    
    setIsSaving(false);
    alert("Profile Protocol Synchronized.");
  };

  const GPA_OPTIONS = ['any', '3.0+', '3.5+', '3.8+', '4.0'];
  const DEGREE_OPTIONS = ['any', 'Undergraduate', 'Graduate (Master\'s)', 'Doctorate (PhD)'];

  return (
    <div className="p-12 lg:p-20 bg-[#F8FAFC] dark:bg-slate-950 min-h-screen transition-colors duration-500 overflow-y-auto font-sans">
        <header className="mb-16">
          <span className="text-[10px] font-black uppercase tracking-[0.3em] text-blue-600 dark:text-blue-400 mb-2 block">Identity Hub</span>
          <h1 className="text-5xl font-black text-slate-900 dark:text-white tracking-tighter">Profile.</h1>
        </header>

        <div className="max-w-4xl space-y-10 pb-20">
           
           {/* System Preferences */}
           <section className="bg-white dark:bg-slate-900 p-8 rounded-[3rem] border border-slate-200 dark:border-slate-800 shadow-lg flex items-center justify-between hover:shadow-xl transition-shadow">
              <div>
                 <h3 className="text-xl font-black text-slate-900 dark:text-white tracking-tight">Interface Mode</h3>
                 <p className="text-xs text-slate-500 font-bold mt-1">Select your preferred visual environment.</p>
              </div>
              
              <button 
                onClick={toggleTheme} 
                className={`relative w-20 h-10 rounded-full transition-colors duration-300 flex items-center px-1 ${isDark ? 'bg-slate-700' : 'bg-slate-200'}`}
              >
                 <div className={`w-8 h-8 bg-white rounded-full shadow-md transform transition-transform duration-300 flex items-center justify-center ${isDark ? 'translate-x-10' : 'translate-x-0'}`}>
                    <i className={`fas ${isDark ? 'fa-moon text-slate-800' : 'fa-sun text-amber-500'} text-xs`}></i>
                 </div>
              </button>
           </section>

           <section className="bg-white dark:bg-slate-900 p-12 rounded-[4rem] border border-slate-200 dark:border-slate-800 shadow-xl space-y-10">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                 <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest px-2">Legal Identity</label>
                    <input type="text" value={user.fullName} disabled className="w-full p-4 rounded-2xl bg-slate-50 dark:bg-slate-800 border-2 border-transparent dark:border-slate-700 font-bold opacity-60 text-slate-900 dark:text-white" />
                 </div>

                 {isPro ? (
                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest px-2">Professional Title</label>
                      <input type="text" value={formData.title || ''} onChange={e => setFormData({ ...formData, title: e.target.value })} className="w-full p-4 rounded-2xl bg-slate-50 dark:bg-slate-800 border-2 border-transparent focus:border-blue-600 dark:focus:border-blue-400 outline-none font-bold text-slate-900 dark:text-white" placeholder="e.g. Senior VP" />
                    </div>
                 ) : (
                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest px-2">Academic Major</label>
                      <input type="text" value={formData.major || ''} onChange={e => setFormData({ ...formData, major: e.target.value })} className="w-full p-4 rounded-2xl bg-slate-50 dark:bg-slate-800 border-2 border-transparent focus:border-blue-600 dark:focus:border-blue-400 outline-none font-bold text-slate-900 dark:text-white" placeholder="e.g. Computer Science" />
                    </div>
                 )}
              </div>

              {!isPro && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                   <div className="space-y-2">
                      <label className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest px-2">Target Networking City</label>
                      <input 
                        type="text" 
                        value={formData.networkingCity || ''} 
                        onChange={e => setFormData({ ...formData, networkingCity: e.target.value })} 
                        className="w-full p-4 rounded-2xl bg-slate-50 dark:bg-slate-800 border-2 border-transparent focus:border-blue-600 dark:focus:border-blue-400 outline-none font-bold text-slate-900 dark:text-white" 
                        placeholder="e.g. New York, San Francisco" 
                      />
                   </div>
                   <div className="space-y-2">
                      <label className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest px-2">Aspiring Job Role</label>
                      <input 
                        type="text" 
                        value={formData.aspiringRole || ''} 
                        onChange={e => setFormData({ ...formData, aspiringRole: e.target.value })} 
                        className="w-full p-4 rounded-2xl bg-slate-50 dark:bg-slate-800 border-2 border-transparent focus:border-blue-600 dark:focus:border-blue-400 outline-none font-bold text-slate-900 dark:text-white" 
                        placeholder="e.g. Product Manager, HFT Developer" 
                      />
                   </div>
                </div>
              )}

              {/* Education History for Professionals */}
              {isPro && (
                <div className="p-8 bg-slate-50 dark:bg-slate-800/50 rounded-[2.5rem] border border-slate-200 dark:border-slate-800 space-y-6">
                   <h4 className="text-sm font-black text-slate-900 dark:text-white uppercase tracking-widest flex items-center gap-3">
                      <i className="fas fa-graduation-cap"></i> Education Origins
                   </h4>
                   <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                         <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Grad School (Univ)</label>
                         <input 
                           type="text" 
                           value={formData.gradUniversity || ''} 
                           onChange={e => setFormData({ ...formData, gradUniversity: e.target.value })} 
                           className="w-full p-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 font-bold text-xs text-slate-900 dark:text-white" 
                           placeholder="e.g. University of Toronto"
                         />
                      </div>
                      <div className="space-y-2">
                         <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Grad Year</label>
                         <input 
                           type="text" 
                           value={formData.gradUniversityYear || ''} 
                           onChange={e => setFormData({ ...formData, gradUniversityYear: e.target.value })} 
                           className="w-full p-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 font-bold text-xs text-slate-900 dark:text-white" 
                           placeholder="e.g. 2018"
                         />
                      </div>
                      <div className="space-y-2">
                         <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">High School</label>
                         <input 
                           type="text" 
                           value={formData.highSchool || ''} 
                           onChange={e => setFormData({ ...formData, highSchool: e.target.value })} 
                           className="w-full p-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 font-bold text-xs text-slate-900 dark:text-white" 
                           placeholder="e.g. Upper Canada College"
                         />
                      </div>
                      <div className="space-y-2">
                         <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">High School Year</label>
                         <input 
                           type="text" 
                           value={formData.highSchoolYear || ''} 
                           onChange={e => setFormData({ ...formData, highSchoolYear: e.target.value })} 
                           className="w-full p-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 font-bold text-xs text-slate-900 dark:text-white" 
                           placeholder="e.g. 2014"
                         />
                      </div>
                   </div>
                </div>
              )}

              {/* Resume / Credentials for Students */}
              {!isPro && (
                <div className="p-8 bg-blue-50 dark:bg-indigo-900/10 rounded-[2.5rem] border border-blue-100 dark:border-indigo-900/30 space-y-6">
                   <h4 className="text-sm font-black text-blue-600 dark:text-indigo-400 uppercase tracking-widest flex items-center gap-3">
                      <i className="fas fa-file-contract"></i> Credentials
                   </h4>
                   <div className="space-y-2">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Resume / Portfolio Link</label>
                      <div className="relative">
                        <i className="fas fa-link absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"></i>
                        <input 
                          type="text" 
                          value={formData.resumeUrl || ''} 
                          onChange={e => setFormData({ ...formData, resumeUrl: e.target.value })} 
                          className="w-full pl-10 pr-4 py-4 rounded-xl border-2 border-blue-100 dark:border-indigo-900/50 bg-white dark:bg-slate-900 font-bold text-sm outline-none focus:border-blue-500 text-slate-900 dark:text-white" 
                          placeholder="Paste link to PDF or LinkedIn (e.g. drive.google.com/...)"
                        />
                      </div>
                      <p className="text-[9px] font-bold text-slate-400 pl-2">Professionals will see this link when you connect.</p>
                   </div>
                </div>
              )}

              <div className="space-y-6">
                 <h4 className="text-sm font-black text-slate-900 dark:text-white uppercase tracking-widest flex items-center gap-3">
                    <i className="fas fa-handshake"></i> Mentorship Alignment
                 </h4>
                 
                 {isPro ? (
                   <div className="space-y-2">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Conversation Topics (What you teach)</label>
                      <input 
                        type="text" 
                        value={Array.isArray(formData.topics) ? formData.topics.join(', ') : formData.topics || ''} 
                        onChange={e => setFormData({ ...formData, topics: e.target.value })} 
                        className="w-full p-4 rounded-2xl bg-slate-50 dark:bg-slate-800 border-2 border-transparent focus:border-blue-600 dark:focus:border-blue-400 outline-none font-bold text-slate-900 dark:text-white" 
                        placeholder="e.g. Portfolio Reviews, Technical Interviews, Salary Negotiation" 
                      />
                   </div>
                 ) : (
                   <div className="space-y-2">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Mentorship Interests (What you want to ask)</label>
                      <input 
                        type="text" 
                        value={Array.isArray(formData.mentorshipTopics) ? formData.mentorshipTopics.join(', ') : formData.mentorshipTopics || ''} 
                        onChange={e => setFormData({ ...formData, mentorshipTopics: e.target.value })} 
                        className="w-full p-4 rounded-2xl bg-slate-50 dark:bg-slate-800 border-2 border-transparent focus:border-blue-600 dark:focus:border-blue-400 outline-none font-bold text-slate-900 dark:text-white" 
                        placeholder="e.g. How to break into HFT, Mock Interviews, Resume Critique" 
                      />
                   </div>
                 )}

                 <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Personal Hobbies</label>
                    <input 
                      type="text" 
                      value={Array.isArray(formData.hobbies) ? formData.hobbies.join(', ') : formData.hobbies || ''} 
                      onChange={e => setFormData({ ...formData, hobbies: e.target.value })} 
                      className="w-full p-4 rounded-2xl bg-slate-50 dark:bg-slate-800 border-2 border-transparent focus:border-blue-600 dark:focus:border-blue-400 outline-none font-bold text-slate-900 dark:text-white" 
                      placeholder="e.g. Chess, Marathon Running, Photography" 
                    />
                 </div>
              </div>

              {isPro && (
                <div className="p-8 bg-blue-50 dark:bg-indigo-900/10 rounded-[2.5rem] border border-blue-100 dark:border-indigo-900/30 space-y-8">
                   <div>
                      <h4 className="text-sm font-black text-blue-600 dark:text-indigo-400 uppercase tracking-widest flex items-center gap-3 mb-2">
                          <i className="fas fa-filter"></i> Inbound Request Filters
                      </h4>
                      <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">
                          Set your inbound gateway. Only student requests matching these criteria will populate your primary dashboard.
                      </p>
                   </div>
                   <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="space-y-2">
                         <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Target GPA</label>
                         <select 
                           value={formData.targetStudentGPA || 'any'} 
                           onChange={e => setFormData({...formData, targetStudentGPA: e.target.value})}
                           className="w-full p-3 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 font-bold text-xs outline-none text-slate-900 dark:text-white"
                         >
                            {GPA_OPTIONS.map(opt => <option key={opt} value={opt}>{opt.toUpperCase()}</option>)}
                         </select>
                      </div>
                      <div className="space-y-2">
                         <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Target Industry</label>
                         <input 
                           type="text"
                           value={formData.targetStudentIndustry || 'any'}
                           onChange={e => setFormData({...formData, targetStudentIndustry: e.target.value})}
                           className="w-full p-3 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 font-bold text-xs outline-none text-slate-900 dark:text-white"
                           placeholder="e.g. Finance, or 'any'"
                         />
                      </div>
                      <div className="space-y-2">
                         <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Preferred Degree</label>
                         <select 
                           value={formData.targetStudentDegree || 'any'} 
                           onChange={e => setFormData({...formData, targetStudentDegree: e.target.value})}
                           className="w-full p-3 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 font-bold text-xs outline-none text-slate-900 dark:text-white"
                         >
                            {DEGREE_OPTIONS.map(opt => <option key={opt} value={opt}>{opt === 'any' ? 'ANY DEGREE' : opt.toUpperCase()}</option>)}
                         </select>
                      </div>
                   </div>
                   <p className="text-[10px] text-slate-400 font-bold italic">Note: Choosing "Any" maximizes mentorship breadth. Specific filters ensure high-fidelity technical alignment.</p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest px-2">{isPro ? 'Current Organization' : 'University'}</label>
                    <input type="text" value={isPro ? (formData.company || '') : (formData.school || '')} onChange={e => setFormData({ ...formData, [isPro ? 'company' : 'school']: e.target.value })} className="w-full p-4 rounded-2xl bg-slate-50 dark:bg-slate-800 border-2 border-transparent focus:border-blue-600 dark:focus:border-blue-400 outline-none font-bold text-slate-900 dark:text-white" />
                 </div>
                 <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest px-2">{isPro ? 'Department/Division' : 'Campus Location'}</label>
                    <input type="text" value={isPro ? (formData.division || '') : (formData.campus || '')} onChange={e => setFormData({ ...formData, [isPro ? 'division' : 'campus']: e.target.value })} className="w-full p-4 rounded-2xl bg-slate-50 dark:bg-slate-800 border-2 border-transparent focus:border-blue-600 dark:focus:border-blue-400 outline-none font-bold text-slate-900 dark:text-white" placeholder={isPro ? "e.g. Capital Markets" : "e.g. Scarborough"} />
                 </div>
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest px-2">Bio / Professional Narrative</label>
                <textarea value={formData.bio || ''} onChange={e => setFormData({ ...formData, bio: e.target.value })} className="w-full p-6 rounded-3xl bg-slate-50 dark:bg-slate-800 border-2 border-transparent focus:border-blue-600 dark:focus:border-blue-400 outline-none h-32 text-slate-900 dark:text-white" />
              </div>

              {isPro && (
                <div className="bg-blue-50 dark:bg-blue-900/20 p-8 rounded-[2.5rem] border border-blue-100 dark:border-blue-900/30">
                   <div className="flex justify-between items-center mb-6">
                      <h4 className="text-sm font-black text-blue-900 dark:text-blue-400 uppercase tracking-widest">Weekly Sync Capacity</h4>
                      <span className="text-2xl font-black text-blue-900 dark:text-blue-300">{formData.maxMeetingsPerWeek || 3} <span className="text-xs uppercase opacity-40">Chats / Week</span></span>
                   </div>
                   <input type="range" min="0" max="10" value={formData.maxMeetingsPerWeek || 3} onChange={e => setFormData({ ...formData, maxMeetingsPerWeek: parseInt(e.target.value) })} className="w-full h-2 bg-blue-200 dark:bg-blue-800 rounded-lg appearance-none cursor-pointer accent-blue-900 dark:accent-blue-400" />
                </div>
              )}

              <button onClick={handleSave} disabled={isSaving} className="w-full py-5 bg-slate-900 dark:bg-blue-600 text-white rounded-3xl font-black text-xs uppercase hover:bg-slate-800 dark:hover:bg-blue-500 transition shadow-xl active:scale-95">
                {isSaving ? <i className="fas fa-circle-notch animate-spin mr-2"></i> : 'Synchronize Identity'}
              </button>
           </section>
        </div>
    </div>
  );
};

export default ProfileView;
